// src/hooks/usePresence.js
// STATUS: active | UPDATED: 2026-09-18
// LOG:
//   2026-09-18  Otto is a real users row now, so he is read rather than
//               synthesised. Agents sort last, never by recency.
//   2026-09-17  Otto rides along, flagged as not a person.
//   2026-09-10  New. Tyler: "If I log in right now and Jazz is logged in still,
//               I should be able to see him logged in. He should be able to see
//               me."
// Who else from your team is here right now.
//
// A HEARTBEAT AND A POLL, NOT REALTIME PRESENCE.
//
// src/lib/supabase.js turns realtime off with eventsPerSecond 0 and says why:
// "it caused production crashes on Node 20 in other portfolio repos". Turning
// that back on to draw a row of avatars is not a trade worth making. This is
// boring, debuggable, and wrong by at most one poll interval, which for "is
// Jazz around" is not wrong in any way anybody can feel.

import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

// Beat faster than we expire, or a tab that is alive flickers offline between
// its own heartbeats. 45 in, 120 out gives two missed beats of slack.
const BEAT_MS = 45000
const POLL_MS = 30000
const ONLINE_WINDOW_SECONDS = 120

export function usePresence(profile) {
  const [team, setTeam] = useState([])
  const userId = profile?.id
  const dealershipId = profile?.dealership_id ?? null
  // A super admin has no dealership, so "my team" for Jazz and Tyler is each
  // other rather than every user in the database. Without this they would see
  // the demo store accounts sitting in their own header.
  const isSuper = profile?.role === 'admin' && !dealershipId

  useEffect(() => {
    if (!userId) return undefined
    let alive = true

    const beat = async () => {
      // Only claim to be here when the tab is actually in front. A phone in a
      // pocket with the app open is not somebody you can get hold of.
      if (document.visibilityState !== 'visible') return
      try {
        await supabase.from('users').update({ last_seen_at: new Date().toISOString() }).eq('id', userId)
      } catch {
        /* a missed beat costs one avatar for one interval */
      }
    }

    const read = async () => {
      const since = new Date(Date.now() - ONLINE_WINDOW_SECONDS * 1000).toISOString()
      // `or` rather than `gte`, because an agent never sends a heartbeat. Otto
      // is here because his row says he is an agent, which is true and is the
      // only honest way to show something that is never not on.
      let q = supabase
        .from('users')
        .select('id, display_name, initials, role, dealership_id, last_seen_at, is_agent')
        .or(`last_seen_at.gte.${since},is_agent.eq.true`)
      q = isSuper ? q.is('dealership_id', null) : q.eq('dealership_id', dealershipId)
      const { data, error } = await q
      if (error || !alive) return
      const people = (data || [])
        .map((p) => ({ ...p, isAgent: p.is_agent === true }))
        .sort((a, b) => {
          // Yourself first, always. Then people by who was seen most recently.
          // Agents last: they are always on, so sorting them by recency would
          // pin them to the front for ever and push real colleagues off the end.
          if (a.id === userId) return -1
          if (b.id === userId) return 1
          if (a.isAgent !== b.isAgent) return a.isAgent ? 1 : -1
          return new Date(b.last_seen_at) - new Date(a.last_seen_at)
        })

      setTeam(people)
    }

    // Beat before the first read or you are missing from your own team.
    beat().then(read)
    const beatTimer = setInterval(beat, BEAT_MS)
    const pollTimer = setInterval(read, POLL_MS)
    // Coming back to the tab should not wait out a poll interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') beat().then(read)
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      alive = false
      clearInterval(beatTimer)
      clearInterval(pollTimer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [userId, dealershipId, isSuper])

  return { team, me: team.find((t) => t.id === userId) || null, others: team.filter((t) => t.id !== userId) }
}
